import { useMemo, useRef, useState } from 'react';

import { useTranslation } from '@mint-lib/i18n';
import { enqueueSnackbar } from '@mint-lib/notifications-api';
import { useMutation, useQuery, useQueryClient } from '@mint-lib/query-client';
import { useFlags } from '@mint-lib/routing-context';
import {
  Button,
  CircularLoadingButton,
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  DrawerPortal,
  DrawerRoot,
  DrawerTitle,
} from '@mint-lib/tw-components';
import {
  BaseFile,
  FileUploader,
  FileUploaderV2RefType,
  ReadyForUploadFile,
  TextArea,
} from '@mint-lib/tw-components/patterns';
import { Chat } from '@mint-sdk/api-ai-assistant';

import {
  FILE_FORMATS,
  MAX_SIZE_BYTES,
} from '../../../constants/uploadingFiles.js';
import { useUpdateFileAccessMutation } from '../../../queries/fileAccess.js';
import type { FileAccessType, FileAccessUser } from '../../../types/rag.js';
import { ShareFilesSection } from '../../KnowledgeBase/ShareFiles/ShareFilesSection.jsx';

type Props = {
  isOpen: boolean;
  onClose: () => void;
};

export const MobileUploadDrawer = ({ isOpen, onClose }: Props) => {
  const { t } = useTranslation('@mint-lib/ai-chat');
  const ff = useFlags('@mint/arm', ['aiFileSystem']);
  const queryClient = useQueryClient();
  const { queryKey: getDocumentQueryKey } = Chat.listRagDocuments({});
  const ref = useRef<FileUploaderV2RefType | null>(null);

  const { data: documents = [] } = useQuery(Chat.listRagDocuments({}));
  const { mutateAsync, isPending } = useMutation({
    mutationFn: (data: Chat.DocumentUploadRequestType) =>
      Chat.loadNewRagDocument({
        data,
        headers: { 'Content-Type': 'multipart/form-data' },
      }),
  });

  const { mutate: updateFileAccess } = useUpdateFileAccessMutation();

  const [uploadedFile, setUploadedFile] = useState<BaseFile[]>([]);
  const [description, setDescription] = useState('');
  const [accessType, setAccessType] = useState<FileAccessType>('invited');
  const [sharedUsers, setSharedUsers] = useState<FileAccessUser[]>([]);

  const onFileSave = async (file: File) => {
    const document = await mutateAsync({ file, description });

    if (ff.aiFileSystem.enabled) {
      updateFileAccess({
        fileId: document.fileId,
        access: { accessType, users: sharedUsers },
      });
    }

    return document;
  };

  const onValidate = (file: File) => {
    const fileIndex = documents.findIndex((d) => d.filename === file.name);

    if (fileIndex !== -1) {
      return Promise.resolve({
        message: 'File name already exists',
        code: '404',
      });
    }

    return Promise.resolve(null);
  };

  const onSnackbar = () => {
    enqueueSnackbar({
      variant: 'success',
      subTitle: t('{{count}} file uploaded to the knowledge base', {
        count: uploadedFile.length,
      }),
      removable: true,
      position: 'top-center',
      className: 'flex self-center',
    });
  };

  const handleUploadFile = async () => {
    if (!ref.current) return;

    const readyForUploadFiles = uploadedFile.filter(
      (file): file is ReadyForUploadFile => file.isReadyForUploadFile(),
    );

    await ref.current.uploadFiles(readyForUploadFiles);
    await queryClient.invalidateQueries({ queryKey: getDocumentQueryKey });

    setDescription('');
    setUploadedFile([]);
    setAccessType('invited');
    setSharedUsers([]);
    onSnackbar();
    onClose();
  };

  const isDisabledUploadBtn = useMemo(() => {
    const areAllFilesReady = uploadedFile.some(
      (file): file is ReadyForUploadFile => file.isReadyForUploadFile(),
    );

    return !uploadedFile.length || !areAllFilesReady;
  }, [uploadedFile]);

  return (
    <Drawer open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DrawerPortal>
        <DrawerOverlay />
        <DrawerRoot
          side="bottom"
          aria-describedby={undefined}
          onOpenAutoFocus={(e) => e.preventDefault()}
          className={
            ff.aiFileSystem.enabled ? 'data-[side=bottom]:h-[90%]' : undefined
          }
        >
          <DrawerHeader onClose={onClose}>
            <DrawerTitle>{t('Upload file')}</DrawerTitle>
            <Button
              variant="contained"
              size="large"
              onClick={handleUploadFile}
              disabled={isPending || isDisabledUploadBtn}
              className="ml-auto"
            >
              {isPending && (
                <CircularLoadingButton size="small" variant="outlined" />
              )}
              {t('Upload')}
            </Button>
          </DrawerHeader>
          <DrawerContent className="flex-col gap-4 overflow-y-auto">
            <FileUploader
              ref={ref}
              fullWidth
              accept={FILE_FORMATS}
              validate={onValidate}
              onChange={setUploadedFile}
              onUpload={onFileSave}
              maxFiles={10}
              maxSize={MAX_SIZE_BYTES}
              variant="button"
            />
            <TextArea
              label={t('Description (Optional)')}
              placeholder={t('Insert a description')}
              onChange={({ target }) => setDescription(target.value)}
              value={description}
              className="min-w-full"
            />
            {ff.aiFileSystem.enabled && (
              <ShareFilesSection
                accessType={accessType}
                onAccessTypeChange={setAccessType}
                sharedUsers={sharedUsers}
                onSharedUsersChange={setSharedUsers}
              />
            )}
          </DrawerContent>
        </DrawerRoot>
      </DrawerPortal>
    </Drawer>
  );
};
